import { cn } from '@lyearn/style';

import {
  InfoPillAppearance,
  InfoPillShape,
  InfoPillSize,
  InfoPillType,
  InfoPillVariant,
} from './types';

const COMMON_OUTLINED_CLASSNAME = 'border border-solid bg-bg-primary';

const INFO_PILL_BACKGROUND_COLOR = {
  filled: {
    blue: 'bg-bg-accent-light',
    cyan: 'bg-bg-cyan-light',
    danger: 'bg-bg-error-light',
    inverse: 'bg-interactive-blur',
    pink: 'bg-bg-pink-light',
    primary: 'bg-bg-tertiary',
    purple: 'bg-bg-purple-light',
    secondary: 'bg-bg-tertiary',
    success: 'bg-bg-success-light',
    warning: 'bg-border-warning',
  },
  outlined: {
    blue: cn(COMMON_OUTLINED_CLASSNAME, 'border-border-accent-dark'),
    cyan: cn(COMMON_OUTLINED_CLASSNAME, 'border-border-cyan-dark'),
    danger: cn(COMMON_OUTLINED_CLASSNAME, 'border-border-error-dark'),
    inverse: 'border border-solid bg-transparent border-interactive-white',
    pink: cn(COMMON_OUTLINED_CLASSNAME, 'border-border-pink-dark'),
    primary: cn(COMMON_OUTLINED_CLASSNAME, 'border-interactive-secondary-hover'),
    purple: cn(COMMON_OUTLINED_CLASSNAME, 'border-border-purple-dark'),
    secondary: cn(COMMON_OUTLINED_CLASSNAME, 'border-interactive-secondary-hover'),
    success: cn(COMMON_OUTLINED_CLASSNAME, 'border-border-success-dark'),
    warning: cn(COMMON_OUTLINED_CLASSNAME, 'border-border-warning-dark'),
  },
};

const INFO_PILL_TYPOGRAPHY_COLOR = {
  blue: 'text-text-accent',
  cyan: 'text-text-cyan',
  danger: 'text-text-error',
  inverse: 'text-white',
  pink: 'text-text-pink',
  primary: 'text-text-primary',
  purple: 'text-text-purple',
  secondary: 'text-text-secondary',
  success: 'text-text-success',
  warning: 'text-text-warning',
};

const INFO_PILL_SIZE_CLASSES = {
  large: 'h-32',
  medium: 'h-24',
};

const INFO_PILL_TYPOGRAPHY_VARIANT = {
  large: 'body-short-02',
  medium: 'body-short-03',
};

const INFO_PILL_SHAPE_CLASS = {
  round: 'rounded-full',
  square: 'rounded-6',
};

const INFO_PILL_ACTIONABLE_PADDING_CLASS = {
  avatar: {
    large: 'px-8',
    medium: 'px-8',
  },
  color: {
    large: 'pe-10 ps-8',
    medium: 'pe-6 ps-8',
  },
  icon: {
    large: 'pe-8 ps-10',
    medium: 'pe-6 ps-8',
  },
  range: {
    large: 'pe-8 ps-12',
    medium: 'pe-6 ps-10',
  },
  text: {
    large: 'pe-8 ps-12',
    medium: 'pe-6 ps-10',
  },
};

const INFO_PILL_PADDING_CLASS = {
  avatar: {
    large: 'pe-10 ps-8',
    medium: 'pe-10 ps-8',
  },
  color: {
    large: 'pe-10 ps-8',
    medium: 'pe-10 ps-8',
  },
  icon: {
    large: 'pe-10 ps-8',
    medium: 'pe-10 ps-8',
  },
  range: {
    large: 'px-10',
    medium: 'px-10',
  },
  text: {
    large: 'px-10',
    medium: 'px-10',
  },
};

const INFO_PILL_ICON_COLOR = {
  blue: '!text-icon-accent',
  cyan: '!text-icon-cyan',
  danger: '!text-icon-error',
  inverse: '!text-icon-white',
  pink: '!text-icon-pink',
  primary: '!text-icon-primary',
  purple: '!text-icon-purple',
  secondary: '!text-icon-secondary',
  success: '!text-icon-success',
  warning: '!text-icon-warning',
};

export const INFO_PILL_DOT_BACKGROUND = {
  blue: 'bg-icon-accent',
  cyan: 'bg-icon-cyan',
  danger: 'bg-icon-error',
  inverse: 'bg-icon-white',
  pink: 'bg-icon-pink',
  primary: 'bg-icon-primary',
  purple: 'bg-icon-purple',
  secondary: 'bg-icon-secondary',
  success: 'bg-icon-success',
  warning: 'bg-icon-warning',
};

export const INFO_PILL_DOT_SIZE = {
  large: 8,
  medium: 6,
};

export const INFO_PILL_ICON_SIZE = {
  large: 16,
  medium: 14,
};

export const INFO_PILL_AVATAR_SIZE = {
  large: 20,
  medium: 16,
};

export const getInfoPillIconColor = (appearance?: InfoPillAppearance) =>
  INFO_PILL_ICON_COLOR[appearance || DEFAULT_INFO_PILL_APPEARANCE];

export const getInfoPillClasses = ({
  actionable,
  appearance,
  shape,
  size,
  type,
  variant,
}: {
  actionable: boolean;
  appearance: InfoPillAppearance;
  shape: InfoPillShape;
  size: InfoPillSize;
  type: InfoPillType;
  variant: InfoPillVariant;
}) => {
  const shapeClass = INFO_PILL_SHAPE_CLASS[shape];
  const sizeVariant = INFO_PILL_SIZE_CLASSES[size];
  const typographyVariant = INFO_PILL_TYPOGRAPHY_VARIANT[size];
  const typographyColor = INFO_PILL_TYPOGRAPHY_COLOR[appearance];
  const backgroundColor = INFO_PILL_BACKGROUND_COLOR[variant][appearance];
  const paddingClass = actionable
    ? INFO_PILL_ACTIONABLE_PADDING_CLASS[type][size]
    : INFO_PILL_PADDING_CLASS[type][size];
  const getInfoPillGap = () => {
    if (type === 'text' && actionable && size === 'medium') {
      return 'gap-4';
    } else if (type === 'range' && actionable) {
      return 'gap-8';
    } else {
      return 'gap-6';
    }
  };

  return {
    deleteIcon: '!m-0',
    icon: '!m-0',
    label: 'p-0',
    root: cn(
      'flex-shrink-0',
      getInfoPillGap(),
      shapeClass,
      sizeVariant,
      typographyVariant,
      typographyColor,
      backgroundColor,
      paddingClass,
    ),
  };
};

export const DEFAULT_INFO_PILL_APPEARANCE: InfoPillAppearance = 'primary';
export const DEFAULT_INFO_PILL_SIZE: InfoPillSize = 'medium';
